import { NO_POS } from '#core/lib/expr/ast.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useEvalExpr } from '#core/lib/expr/session/useEvalExpr.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import { expectDataset, isLazy } from '#core/lib/expr/value.ts';

import { DescribeViewsTool } from '#plugin-chat/components/tools/describe-views/DescribeViewsTool.tsx';
import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import {
  type QueryKnowledgeGraphInput,
  QueryKnowledgeGraphInputSchema,
  type QueryKnowledgeGraphOutput,
} from '#plugin-chat/components/tools/query-knowledge-graph/fetch.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { summarizeDataset } from '#plugin-chat/lib/tool/dataset-summary.ts';
import { stageDatasetBinding } from '#plugin-chat/lib/tool/datasetBinding.ts';
import { DatasetPreview, ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * QueryKnowledgeGraphTool — s-expr-driven CDF Data Model query.
 *
 * The LLM writes an s-expression that evaluates to a `Dataset`
 * (typically a `LazyDataset` over a `QkgSource`). `execute` evaluates
 * **and materialises** it (one CDF round-trip), then returns a generic
 * `DatasetSummary` (column profile + row sample + data-quality notes)
 * so the LLM sees real data and any CDF failure surfaces through the
 * structured error envelope. The materialised result is **staged** as
 * an immutable replay recipe under `args.outputDataset` via
 * `stageDatasetBinding`; the `<Tool>` execute boundary commits it only
 * if this success envelope is actually returned, so a failed/errored
 * call never leaves or persists a binding. Downstream tools then
 * reference it by name (`(transform (col <outputDataset>) …)`) instead
 * of re-passing the s-expression. The recipe is persisted to
 * localStorage and replayed on session (re)creation, so a page reload /
 * thread switch restores prior datasets. Bindings are immutable:
 * reusing an `outputDataset` name throws `SessionRebindingError` at
 * commit (a recoverable tool error carrying a concretely-free suggested
 * name), keeping prior `(col …)` references stable. The
 * materialised result is also cached on `session.fetch` so `render`'s
 * `useMaterializedExpr` reuses it without a second round-trip.
 *
 * No per-call EvalContext assembly — `<ChatSessionProvider>` populates
 * `EvalDepsContext` once for the whole conversation, and every hook
 * below reads from it.
 *
 * The full s-expression grammar is documented once in the shared
 * `<ExprDslReference>` system-prompt block (see `AtlasInstructions`),
 * not re-embedded per tool.
 */
export const QueryKnowledgeGraphTool = withMention('queryKnowledgeGraph', function QueryKnowledgeGraphToolImpl() {
  const evalExpr = useEvalExpr();
  const materialize = useMaterialize();
  // Session needed to stage the materialised result under outputDataset
  // so downstream tools reference it by name (parity with TransformDataset).
  const session = useSession();
  return (
    <Tool<QueryKnowledgeGraphInput, QueryKnowledgeGraphOutput>
      description={
        <ToolGuidance
          tool={QueryKnowledgeGraphTool}
          whenToUse={[
            'Look up entities by name (Entity view)',
            'Discover relationships (FlatHierarchy view)',
            'Get KPI data (latest datapoints), timeseries references, entity metadata (requires query reference or instanceIds)',
          ]}
        >
          <li>
            <strong>
              Mandatory rule: Always call the {DescribeViewsTool.mention} tool first for all relevant views before
              calling
              {QueryKnowledgeGraphTool.mention}.
            </strong>
            This is a very important step to ensure you query the correct views and reference correct column names.
          </li>
          <p>
            Query a CDF Data Model view via an s-expression. The expression typically wraps a single{' '}
            <code>(query-knowledge-graph "View" :where ... :sort [...] :limit N)</code> call but any s-expression
            returning a Dataset is accepted. The query is executed and the result materialised — you receive a column
            profile and a small row sample. The result is recorded as an immutable, persisted binding under{' '}
            <code>outputDataset</code>; reference it by that name in downstream <code>transformDataset</code>/
            <code>showDashboard</code> calls (e.g. <code>(transform &lt;outputDataset&gt; ...)</code>) — do not re-pass
            the s-expression. Pick a <strong>unique</strong> <code>outputDataset</code> name: bindings are immutable, so
            reusing a name is an error. Bindings persist across page reloads and thread switches.
          </p>
          <example name="kg-query-to-dashboard">
            <p>User: "Show the 10 highest-capacity production wells."</p>
            <ol>
              <li>
                <code>queryKnowledgeGraph</code> — expression{' '}
                <code>
                  (query-knowledge-graph "WellAsset" :where (= type "production") :sort [(desc capacity)] :limit 10)
                </code>
                , outputDataset <code>top-wells</code> → binds <code>top-wells</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <code>
                  (dashboard (table (column :ref (col top-wells name) :label "Well" :format text) (column :ref (col
                  top-wells capacity) :label "Capacity" :format number) :span [12 6]))
                </code>
              </li>
            </ol>
          </example>
          <p>
            <strong>Column references inside `:where`.</strong> A <em>scalar</em> column of the view you are querying is
            a bare symbol — <code>(= entityType "field")</code>, <code>(prefix name "STORM")</code>. A <em>relation</em>{' '}
            column of that same view (a <code>ref&lt;entity&gt;</code> such as <code>field</code>/<code>well</code>/
            <code>asset</code>) is <code>(col &lt;ThisView&gt; &lt;relationColumn&gt; externalId)</code> and may only be
            the <strong>left</strong> side of an <code>in</code>/<code>=</code>. A column from a <em>different</em>{' '}
            bound dataset is <code>(col &lt;binding&gt; &lt;column&gt;)</code> and goes on the <strong>right</strong>.
            To filter a view by ids another query produced, combine the two:{' '}
            <code>(in (col &lt;ThisView&gt; &lt;relationColumn&gt; externalId) (col &lt;binding&gt; externalId))</code>.
            Do <strong>not</strong> compare a relation column to a bare id string, and do not put the queried view's own
            name where a binding is expected.
          </p>
          <p>
            <strong>Zero rows on a relation filter is a guess, not an answer.</strong> If a query whose{' '}
            <code>:where</code> constrains a <code>ref&lt;entity&gt;</code> relation column returns 0 rows and you did{' '}
            <strong>not</strong> chain that relation against a prior binding's ids via{' '}
            <code>(in (col &lt;ThisView&gt; &lt;relationColumn&gt; externalId) (col &lt;binding&gt; externalId))</code>{' '}
            — i.e. you compared it to a literal id string or guessed a space/externalId — that is not a "no data"
            result. Re-query the Entity view to obtain real instanceIds, bind them, then chain the relation filter
            against that binding before concluding the data does not exist.
          </p>
          <example name="kg-cross-dataset-relation-filter">
            <p>User: "Look up the STORM EYE field, then list its flat-hierarchy entries (wells/strings)."</p>
            <ol>
              <li>
                <code>describeViews</code> — confirm <code>Entity_Agent</code> and <code>FlatHierarchy_Agent</code>{' '}
                columns. <code>FlatHierarchy_Agent.field</code> is a <code>ref&lt;entity&gt;</code> relation.
              </li>
              <li>
                <code>queryKnowledgeGraph</code> — expression{' '}
                <code>
                  (query-knowledge-graph "Entity_Agent" :where (and (= entityType "field") (prefix name "STORM EYE"))
                  :limit 50)
                </code>
                , outputDataset <code>storm-eye-field</code> → binds <code>storm-eye-field</code>
              </li>
              <li>
                <code>queryKnowledgeGraph</code> — expression{' '}
                <code>
                  (query-knowledge-graph "FlatHierarchy_Agent" :where (in (col FlatHierarchy_Agent field externalId)
                  (col storm-eye-field externalId)))
                </code>
                , outputDataset <code>storm-eye-hierarchy</code>. The relation LHS{' '}
                <code>(col FlatHierarchy_Agent field externalId)</code> names the queried view; the set RHS{' '}
                <code>(col storm-eye-field externalId)</code> names the prior binding.
              </li>
            </ol>
          </example>
        </ToolGuidance>
      }
      display={{
        running: 'Querying knowledge graph...',
        progress: 'looking up data',
        complete: { label: 'Query complete', semantic: 'view-query' },
        error: (props) => `Query error: ${props.result && 'error' in props.result && props.result?.error}`,
      }}
      execute={async (args: QueryKnowledgeGraphInput): Promise<QueryKnowledgeGraphOutput> => {
        // Eval builds the (usually lazy) Dataset; capture the source
        // label from the plan before materialising. `materialize` forces
        // the CDF round-trip and is memoised on `session.fetch`, so the
        // later `render` + any chained tool reuse this result.
        const value = await evalExpr(args.expression);
        const ds = expectDataset(value, 'queryKnowledgeGraph result', NO_POS);
        const source = isLazy(ds) ? ds.plan.source.kind : 'materialized';
        const sourceLabel = isLazy(ds) && ds.plan.source.kind === 'qkg' ? ds.plan.source.view : source;
        const mat = await materialize(ds);
        // Stage the binding on the success envelope; the <Tool> boundary
        // commits it as the final step iff this success result is
        // actually returned — so a failed/errored call can never leave
        // or persist a binding. A duplicate name throws
        // SessionRebindingError at commit; the boundary routes it to the
        // structured error envelope.
        return stageDatasetBinding(
          {
            success: true,
            state: mat.state,
            source: sourceLabel,
            outputDataset: args.outputDataset,
            summary: summarizeDataset(mat),
          },
          session,
          args.outputDataset,
          args.expression,
          mat,
        );
      }}
      message={{
        call: ({ args }) => (
          <p>
            Querying the knowledge graph → <code>{args.outputDataset}</code>: <code>{args.expression}</code>
          </p>
        ),
        result: ({ output }) => (
          <>
            <p>
              {`Resolved a ${output.state} dataset from ${output.source}, recorded as immutable binding "${output.outputDataset}" (persisted; survives reload). Reference ${output.outputDataset} by name in downstream transformDataset / showDashboard calls — do not re-pass the s-expression, and do not reuse this name.`}
            </p>
            <DatasetPreview summary={output.summary} />
          </>
        ),
        error: ({ args, envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            recovery={
              <p>
                Expression: <code>{args.expression}</code>. Fix the s-expression — verify the view name and{' '}
                <code>:where</code> predicate against <code>explainViews</code> — then retry. Do not ask the user.
              </p>
            }
            toolName={toolName}
          />
        ),
      }}
      name="queryKnowledgeGraph"
      parameters={QueryKnowledgeGraphInputSchema}
    />
  );
});
